/*
 * Mara League OS — Broadcast Overlay Engine (Phase 4 · §10)
 * ----------------------------------------------------------
 * PUBLIC route (no auth). Loaded as a Browser Source in OBS/vMix or as an
 * iframe in the admin Broadcast Studio preview.
 *
 * URL params:
 *   layout   scorebug_bottom | scorebug_top | corner_minimal | led_stadium
 *            | obs_stream_full | scorebug_only | shot_clock_only |
 *            lower_third_only | sponsors_only
 *   bg       "transparent" (default) | "chroma" (magenta bg for keying)
 *   theme    "auto" (tenant colors) | "dark" | "kenya"
 *   compact  1 | 0
 *
 * The page polls `/api/public/broadcast/telemetry/:match_id` every 500 ms
 * (§12 latency target). If match_id is "current" we auto-resolve via
 * /api/public/broadcast/current so a single URL can be pinned in OBS.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";

const POLL_MS = 500;

function fmtClock(secs) {
  const s = Math.max(0, Math.round(secs || 0));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/* ============================================================
 * SetTicker — running per-set scoreline (6-3 · 6-4 · …) that
 * appears inside the ScoreBug sub-row the moment at least one
 * set has recorded games. iter82.
 * ============================================================ */
function SetTicker({ t, a, b }) {
  const raw = Array.isArray(t.tennis_sets) ? t.tennis_sets : [];
  // Filter to sets with any activity — pure {a:0,b:0} rows are placeholders.
  const shown = raw
    .map((s, i) => ({
      idx: i + 1,
      a: Number(s?.a) || 0,
      bv: Number(s?.b) || 0,
      inProgress: !!s?.in_progress,
    }))
    .filter((s) => s.a > 0 || s.bv > 0);
  if (shown.length === 0) return null;
  return (
    <div
      className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-widest px-2 flex-wrap"
      data-testid="broadcast-set-ticker"
    >
      <span className="text-white/50 font-bold">Sets</span>
      {shown.map((s) => {
        const winner = s.inProgress
          ? null
          : s.a > s.bv
          ? "a"
          : s.bv > s.a
          ? "b"
          : null;
        const color =
          winner === "a"
            ? a.brand_color
            : winner === "b"
            ? b.brand_color
            : "rgba(255,255,255,0.6)";
        return (
          <span
            key={s.idx}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono font-black text-white bg-black/45 ring-1"
            style={{ boxShadow: `inset 0 0 0 1px ${color}55` }}
            data-testid={`broadcast-set-${s.idx}`}
          >
            <span className="text-[9px] font-anton tracking-widest text-white/50">
              S{s.idx}
            </span>
            <span className="tabular-nums" style={{ color: winner === "a" ? color : "#fff" }}>
              {s.a}
            </span>
            <span className="text-white/40">-</span>
            <span className="tabular-nums" style={{ color: winner === "b" ? color : "#fff" }}>
              {s.bv}
            </span>
            {s.inProgress && (
              <span className="text-[8px] font-bold text-red-300 ml-0.5">•LIVE</span>
            )}
          </span>
        );
      })}
    </div>
  );
}

/* ============================================================
 * SeedStripe — vertical accent stripe on the left edge of the frame.
 * Iter79: replaces the need for a lower-third — viewers instantly see
 * which pair (Seed 1 / 2 / 3) is on court by color + rotated label.
 * ============================================================ */
const SEED_STRIPE = {
  1: { color: "#F4B400", label: "SEED 1" },
  2: { color: "#00A86B", label: "SEED 2" },
  3: { color: "#7DD3FC", label: "SEED 3" },
};
function SeedStripe({ t }) {
  const seed = t.match?.seed_number;
  const meta = SEED_STRIPE[seed];
  if (!meta) return null;
  return (
    <div
      className="absolute top-0 bottom-0 left-0 pointer-events-none flex flex-col items-center justify-center"
      style={{
        width: 56,
        background: `linear-gradient(180deg, ${meta.color}f2, ${meta.color}c8 50%, ${meta.color}f2)`,
        boxShadow: `inset -1px 0 0 rgba(0,0,0,0.35), 2px 0 14px ${meta.color}66`,
      }}
      data-testid={`broadcast-seed-stripe-${seed}`}
    >
      <div
        className="font-anton uppercase text-black/85 font-black leading-none"
        style={{
          writingMode: "vertical-rl",
          transform: "rotate(180deg)",
          fontSize: 34,
          letterSpacing: "0.18em",
        }}
      >
        {meta.label}
      </div>
    </div>
  );
}

/* ============================================================
 * ScoreBug — main lower-third scoreboard
 * ============================================================ */
function ScoreBug({ t, position = "bottom-center", style = "kenya-red-green" }) {
  const a = t.teams.a, b = t.teams.b;
  const winning = t.score.leader;
  const primaryFg = t.tenant?.theme_config?.brand_ink || "#fff";
  const bgGrad = style === "monochrome"
    ? "linear-gradient(90deg, rgba(15,15,15,0.94), rgba(0,0,0,0.94))"
    : style === "electric-blue"
    ? "linear-gradient(90deg, rgba(3,20,42,0.94), rgba(2,8,25,0.94))"
    : style === "amber-black"
    ? "linear-gradient(90deg, rgba(30,20,3,0.94), rgba(0,0,0,0.94))"
    : "linear-gradient(90deg, rgba(20,4,4,0.95), rgba(0,0,0,0.95) 40%, rgba(4,20,10,0.95))";

  const positionCls =
    position === "bottom-left"   ? "left-8 bottom-8" :
    position === "bottom-right"  ? "right-8 bottom-8" :
    position === "top-center"    ? "left-1/2 -translate-x-1/2 top-8" :
                                    "left-1/2 -translate-x-1/2 bottom-8";

  return (
    <div className={`absolute ${positionCls} pointer-events-none`}>
      <div className="flex items-stretch shadow-2xl ring-1 ring-white/10 backdrop-blur-md"
           style={{ background: bgGrad, minWidth: 640 }}>
        {/* Team A */}
        <div className={`flex items-center gap-3 px-6 py-3 ${winning === "a" ? "" : "opacity-90"}`}
             style={{ borderLeft: `6px solid ${a.brand_color}` }}>
          {a.logo_url ? (
            <img src={a.logo_url} alt="" className="w-10 h-10 object-contain" />
          ) : (
            <div className="w-10 h-10 rounded flex items-center justify-center text-white text-sm font-black"
                 style={{ background: a.brand_color }}>{a.short_name}</div>
          )}
          <div className="text-white font-anton text-2xl tracking-tight uppercase">{a.short_name}</div>
        </div>
        <div className="px-4 py-3 min-w-[64px] text-center border-x border-white/10"
             style={{ background: winning === "a" ? `${a.brand_color}20` : "transparent" }}>
          <div className="font-mono font-black text-4xl leading-none text-white">{t.score.a}</div>
          {t.tennis_display && (
            <div
              className="mt-1 font-anton text-xl leading-none tabular-nums text-[#F4B400]"
              data-testid="broadcast-tennis-a"
            >
              {t.tennis_display.label === "DEUCE"
                ? "40"
                : t.tennis_display.label === "ADVANTAGE A"
                ? "AD"
                : t.tennis_display.a}
            </div>
          )}
        </div>
        {/* Middle — clock + period + live-game-state chip */}
        <div className="px-6 py-3 flex flex-col items-center justify-center border-r border-white/10">
          <div className="text-[9px] uppercase tracking-widest text-zinc-400">Period {t.clocks.period} / {t.match.num_periods}</div>
          <div className="font-mono font-bold text-2xl leading-none text-white">
            {fmtClock(t.clocks.period_remaining_seconds)}
          </div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">
            Master {fmtClock(t.clocks.master_remaining_seconds)}
          </div>
          {t.tennis_display?.label && (
            <div
              className="mt-1 text-[9px] uppercase tracking-widest font-bold text-[#F4B400] bg-[#F4B400]/10 px-1.5 py-0.5 rounded"
              data-testid="broadcast-tennis-label"
            >
              {t.tennis_display.label}
            </div>
          )}
        </div>
        {/* Team B */}
        <div className="px-4 py-3 min-w-[64px] text-center border-r border-white/10"
             style={{ background: winning === "b" ? `${b.brand_color}20` : "transparent" }}>
          <div className="font-mono font-black text-4xl leading-none text-white">{t.score.b}</div>
          {t.tennis_display && (
            <div
              className="mt-1 font-anton text-xl leading-none tabular-nums text-[#00A86B]"
              data-testid="broadcast-tennis-b"
            >
              {t.tennis_display.label === "DEUCE"
                ? "40"
                : t.tennis_display.label === "ADVANTAGE B"
                ? "AD"
                : t.tennis_display.b}
            </div>
          )}
        </div>
        <div className={`flex items-center gap-3 px-6 py-3 ${winning === "b" ? "" : "opacity-90"}`}
             style={{ borderRight: `6px solid ${b.brand_color}` }}>
          <div className="text-white font-anton text-2xl tracking-tight uppercase">{b.short_name}</div>
          {b.logo_url ? (
            <img src={b.logo_url} alt="" className="w-10 h-10 object-contain" />
          ) : (
            <div className="w-10 h-10 rounded flex items-center justify-center text-white text-sm font-black"
                 style={{ background: b.brand_color }}>{b.short_name}</div>
          )}
        </div>
      </div>
      {/* Sub-line: service indicator + court */}
      <div className="mt-1 flex items-center gap-4 text-[10px] uppercase tracking-widest px-2">
        <span className="text-white/60">Court: <span className="text-white">{t.match.court}</span></span>
        {t.service.serving_team_id && (
          <span className="text-white/60">
            Serving:{" "}
            <span className="text-amber-400">
              {t.service.serving_team_id === a.id ? a.short_name : b.short_name}
            </span>
            <span className="ml-2 text-white/50">
              {t.service.points_since_last_change}/{t.service.every}
            </span>
          </span>
        )}
      </div>
      {/* iter82 · Live per-set scoreline ticker — appears the moment a set
       * has any recorded games (a>0 or b>0) so OBS viewers see the running
       * 6-3 · 6-4 · ... breakdown without needing a lower-third. */}
      <SetTicker t={t} a={a} b={b} />
      {/* Golden armed indicator */}
      {(t.flags.golden_armed_a || t.flags.golden_armed_b) && (
        <div className="mt-1 text-center text-xs uppercase tracking-widest font-bold text-amber-400 animate-pulse">
          {t.flags.clash_armed ? "⚡ CLASH — 4× POINTS" : "★ GOLDEN POINT ARMED"}
        </div>
      )}
    </div>
  );
}

/* ============================================================
 * ShotClockCircle — big circle in corner
 * ============================================================ */
function ShotClockCircle({ t }) {
  const rem = t.clocks.shot_clock_seconds;
  const limit = t.clocks.shot_clock_limit || 15;
  const pct = Math.max(0, Math.min(1, rem / limit));
  const danger = pct < 0.34;
  const size = 120;
  const radius = 52;
  const circ = 2 * Math.PI * radius;
  const running = t.clocks.shot_clock_running;
  if (!running && rem === 0) return null;
  return (
    <div className="absolute top-8 right-8 pointer-events-none">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} strokeWidth={8}
            stroke="rgba(255,255,255,0.08)" fill="rgba(0,0,0,0.6)" />
          <circle cx={size / 2} cy={size / 2} r={radius} strokeWidth={8}
            stroke={danger ? "#f43f5e" : "#f59e0b"} fill="none"
            strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 300ms linear" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className={`font-mono font-black text-3xl leading-none ${danger ? "text-red-400 animate-pulse" : "text-white"}`}>
            {rem}
          </div>
          <div className="text-[9px] uppercase tracking-widest text-white/50">Shot</div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * SponsorTicker — rotating sponsor logos, bottom row
 * ============================================================ */
function SponsorTicker({ sponsors, rotation }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!sponsors?.length) return;
    const iv = setInterval(() => setIdx((i) => (i + 1) % sponsors.length), rotation * 1000);
    return () => clearInterval(iv);
  }, [sponsors, rotation]);
  if (!sponsors?.length) return null;
  const s = sponsors[idx];
  return (
    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 pointer-events-none">
      <div className="px-4 py-2 rounded flex items-center gap-3 bg-black/70 backdrop-blur ring-1 ring-white/10">
        <div className="text-[9px] uppercase tracking-widest text-amber-500">Powered by</div>
        {s.logo_url ? (
          <img src={s.logo_url} alt={s.name} className="h-6 object-contain" />
        ) : (
          <div className="text-white font-anton text-lg uppercase tracking-tight">{s.name}</div>
        )}
        <div className="text-[9px] uppercase tracking-widest text-zinc-500">{s.tier}</div>
      </div>
    </div>
  );
}

/* ============================================================
 * WaterBreakBanner + PeriodTransitionBanner (full-width)
 * ============================================================ */
function EventBanner({ t }) {
  let text = null; let color = "bg-blue-600"; let sub = null;
  if (t.flags.water_break_active && t.clocks.water_break_seconds > 0) {
    text = "WATER BREAK"; sub = fmtClock(t.clocks.water_break_seconds); color = "bg-sky-600";
  } else if (t.flags.side_change_active && t.clocks.side_change_seconds > 0) {
    text = "SIDE CHANGE"; sub = fmtClock(t.clocks.side_change_seconds); color = "bg-purple-600";
  } else if (t.flags.injury_active_a || t.flags.injury_active_b) {
    text = "INJURY TIMEOUT"; sub = fmtClock(Math.max(t.clocks.injury_a_seconds, t.clocks.injury_b_seconds));
    color = "bg-rose-600";
  } else if (t.flags.strategic_timeout_a || t.flags.strategic_timeout_b) {
    text = "STRATEGIC TIMEOUT"; color = "bg-amber-600";
  } else if (t.flags.tiebreaker_active) {
    text = "SUDDEN DEATH"; sub = fmtClock(t.clocks.tiebreaker_seconds); color = "bg-red-700";
  } else if (t.flags.ultimate_golden_point) {
    text = "ULTIMATE GOLDEN POINT"; color = "bg-gradient-to-r from-yellow-500 to-red-600";
  }
  if (!text) return null;
  return (
    <div className={`absolute top-1/3 left-0 right-0 py-8 ${color} shadow-2xl pointer-events-none`}>
      <div className="text-center">
        <div className="font-anton text-6xl uppercase tracking-tighter text-white leading-none animate-pulse">
          {text}
        </div>
        {sub && <div className="font-mono text-3xl text-white/90 mt-2">{sub}</div>}
      </div>
    </div>
  );
}

/* ============================================================
 * LowerThird — team names + active players
 * ============================================================ */
function LowerThird({ t }) {
  const a = t.teams.a, b = t.teams.b;
  return (
    <div className="absolute bottom-40 left-8 pointer-events-none">
      <div className="flex flex-col gap-2">
        {[a, b].map((team) => (
          <div key={team.id} className="flex items-center gap-3 px-4 py-2 bg-black/70 backdrop-blur ring-1 ring-white/10"
            style={{ borderLeft: `4px solid ${team.brand_color}` }}>
            <div className="text-white font-anton text-lg uppercase">{team.name}</div>
            {team.active_players?.length ? (
              <div className="flex gap-2 text-xs text-zinc-300">
                {team.active_players.slice(0, 2).map((p) => (
                  <span key={p.id}>{p.name}</span>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
 * Master Overlay page
 * ============================================================ */
export default function BroadcastOverlay() {
  const { matchId: rawMatchId } = useParams();
  const [sp] = useSearchParams();
  const layoutOverride = sp.get("layout");
  const bg = sp.get("bg") || "transparent";
  const [matchId, setMatchId] = useState(rawMatchId === "current" ? null : rawMatchId);
  const [telemetry, setTelemetry] = useState(null);
  const [sponsors, setSponsors] = useState({ sponsors: [], rotation_seconds: 15, show_rotation: true });
  const [err, setErr] = useState(null);
  const mounted = useRef(true);

  // Set global transparent bg on <html> and <body> for OBS chroma keying
  useEffect(() => {
    const prevHtml = document.documentElement.style.background;
    const prevBody = document.body.style.background;
    if (bg === "chroma") {
      document.documentElement.style.background = "#ff00ff";
      document.body.style.background = "#ff00ff";
    } else {
      document.documentElement.style.background = "transparent";
      document.body.style.background = "transparent";
    }
    return () => {
      document.documentElement.style.background = prevHtml;
      document.body.style.background = prevBody;
    };
  }, [bg]);

  // Resolve "current" match id from the public endpoint
  useEffect(() => {
    if (rawMatchId !== "current") return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/public/broadcast/current");
        if (alive && data.match_id) setMatchId(data.match_id);
      } catch { /* silent */ }
    };
    tick();
    const iv = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, [rawMatchId]);

  // Poll telemetry
  useEffect(() => {
    mounted.current = true;
    if (!matchId) return;
    let iv;
    const tick = async () => {
      try {
        const { data } = await api.get(`/public/broadcast/telemetry/${matchId}`);
        if (mounted.current) { setTelemetry(data); setErr(null); }
      } catch (e) {
        if (mounted.current) setErr(e?.response?.status === 404 ? "Match not found" : "Feed offline");
      }
    };
    tick();
    iv = setInterval(tick, POLL_MS);
    return () => { mounted.current = false; clearInterval(iv); };
  }, [matchId]);

  // Poll sponsors once per minute (they rarely change during a match)
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/public/broadcast/sponsors");
        if (alive) setSponsors(data);
      } catch { /* silent */ }
    };
    tick();
    const iv = setInterval(tick, 60_000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const layout = useMemo(() => {
    if (layoutOverride) return layoutOverride;
    return telemetry?.config?.broadcast_overlay_layout || "scorebug_bottom";
  }, [layoutOverride, telemetry]);

  // Elements enabled per layout preset (§13 + spec §10)
  const show = useMemo(() => {
    if (!telemetry) return {};
    const cfg = telemetry.config;
    const l = layout;
    const base = {
      scorebug: true,
      shot_clock: cfg.broadcast_show_shot_clock,
      sponsors: cfg.broadcast_show_sponsor_rotation && sponsors.show_rotation,
      lower_third: true,
      event_banner: true,
      // iter79 · Broadcast Overlay Seed Stripe. Always render when the
      // match has a seed_number (1/2/3) — regardless of layout preset.
      seed_stripe: !!telemetry.match?.seed_number,
    };
    if (l === "scorebug_only")      return { scorebug: true, event_banner: true, seed_stripe: base.seed_stripe };
    if (l === "shot_clock_only")    return { shot_clock: true };
    if (l === "lower_third_only")   return { lower_third: true, seed_stripe: base.seed_stripe };
    if (l === "sponsors_only")      return { sponsors: true };
    if (l === "corner_minimal")     return { scorebug: true, shot_clock: base.shot_clock, seed_stripe: base.seed_stripe };
    if (l === "led_stadium")        return { scorebug: true, event_banner: true, sponsors: base.sponsors, seed_stripe: base.seed_stripe };
    if (l === "obs_stream_full")    return { ...base };
    // scorebug_top / scorebug_bottom
    return { ...base };
  }, [telemetry, layout, sponsors]);

  const position = useMemo(() => {
    if (layout === "scorebug_top") return "top-center";
    return telemetry?.config?.broadcast_scorebug_position || "bottom-center";
  }, [layout, telemetry]);

  if (!matchId) {
    return (
      <div className="fixed inset-0 flex items-center justify-center" style={{ background: bg === "chroma" ? "#ff00ff" : "transparent" }}>
        <div className="text-center">
          <div className="text-white/80 text-xl font-anton uppercase tracking-widest">No live match</div>
          <div className="text-white/40 text-xs mt-2">The overlay stays clean until a match goes live.</div>
        </div>
      </div>
    );
  }
  if (err || !telemetry) {
    return (
      <div className="fixed inset-0 flex items-center justify-center" style={{ background: bg === "chroma" ? "#ff00ff" : "transparent" }}>
        <div className="text-red-500 text-xs uppercase tracking-widest">{err || "Loading…"}</div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 overflow-hidden"
      style={{
        background: bg === "chroma" ? "#ff00ff" : "transparent",
        fontFamily: telemetry.tenant?.theme_config?.body_font || "Inter, system-ui, sans-serif",
      }}
      data-testid="broadcast-overlay-root"
    >
      {show.event_banner && <EventBanner t={telemetry} />}
      {show.lower_third && <LowerThird t={telemetry} />}
      {show.shot_clock && <ShotClockCircle t={telemetry} />}
      {show.seed_stripe && <SeedStripe t={telemetry} />}
      {show.scorebug && (
        <ScoreBug t={telemetry} position={position} style={telemetry.config.broadcast_lower_third_style} />
      )}
      {show.sponsors && (
        <SponsorTicker sponsors={sponsors.sponsors} rotation={sponsors.rotation_seconds} />
      )}
    </div>
  );
}
